import { useState, useEffect, useRef } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

interface ApiCacheOptions {
  ttl?: number; // Time to live in milliseconds
  enabled?: boolean;
}

// Global cache store
const cacheStore = new Map<string, CacheEntry<any>>();

export const useApiCache = <T>(
  cacheKey: string,
  fetchFn: () => Promise<T>,
  options: ApiCacheOptions = {}
) => {
  const { ttl = 30000, enabled = true } = options; // Default 30 seconds
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const isExpired = (entry: CacheEntry<T>): boolean => {
    return Date.now() - entry.timestamp > entry.ttl;
  };

  const getCachedData = (): T | null => {
    if (!enabled) return null;
    
    const entry = cacheStore.get(cacheKey);
    if (entry && !isExpired(entry)) {
      return entry.data;
    }
    
    if (entry && isExpired(entry)) {
      cacheStore.delete(cacheKey);
    }
    
    return null;
  };

  const setCachedData = (newData: T) => {
    if (!enabled) return;
    
    cacheStore.set(cacheKey, {
      data: newData,
      timestamp: Date.now(),
      ttl
    });
  };

  const fetchData = async (forceRefresh = false) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Check cache first
    if (!forceRefresh) {
      const cachedData = getCachedData();
      if (cachedData) {
        setData(cachedData);
        setError(null);
        return;
      }
    }

    setLoading(true);
    setError(null);

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      const result = await fetchFn();
      setData(result);
      setCachedData(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      
      console.error(`API error for ${cacheKey}:`, err);
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata');
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const invalidateCache = () => {
    cacheStore.delete(cacheKey);
  };

  const clearAllCache = () => {
    cacheStore.clear();
  };

  useEffect(() => {
    fetchData();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [cacheKey]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(true),
    invalidateCache,
    clearAllCache
  };
};

// Utility function to clear all cache
export const clearAllApiCache = () => {
  cacheStore.clear();
};

// Utility function to get cache stats
export const getCacheStats = () => {
  const now = Date.now();
  let validEntries = 0;
  let expiredEntries = 0;
  
  cacheStore.forEach(entry => {
    if (now - entry.timestamp > entry.ttl) {
      expiredEntries++;
    } else {
      validEntries++;
    }
  });
  
  return {
    total: cacheStore.size,
    valid: validEntries,
    expired: expiredEntries
  };
};
